import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, Send, User } from "lucide-react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface EarningForSelection {
  id: number;
  amount_earned: number;
  created_at: string;
  commission_at_time_of_transaction: number;
  commission_type_at_time_of_transaction: "percentage" | "fixed";
  promotions?: {
    id: number;
    name: string;
    code: string;
  };
  user_subscriptions?: {
    id: number;
    price: number;
    currency_code: string;
    user_id: number;
    subscription_plan_id: number;
    status: string;
    subscription_start: string;
  };
}

interface CustomerProfile {
  id: number;
  name: string | null;
  email: string | null;
  avatar_url: string | null;
}

interface SubscriptionPlan {
  id: number;
  name: string;
  duration: number;
  duration_type: string;
}

interface PayoutSelectionDialogProps {
  collaboratorId: string;
  pendingAmount: number;
  onPayoutCreated: () => void;
  formatCurrency: (amount: number) => string;
  isCreating: boolean;
  setIsCreating: (loading: boolean) => void;
}

const PayoutSelectionDialog = ({
  collaboratorId,
  pendingAmount,
  onPayoutCreated,
  formatCurrency,
  isCreating,
  setIsCreating,
}: PayoutSelectionDialogProps) => {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [earnings, setEarnings] = useState<EarningForSelection[]>([]);
  const [selectedEarnings, setSelectedEarnings] = useState<Set<number>>(
    new Set()
  );
  const [customerProfiles, setCustomerProfiles] = useState<
    Record<number, CustomerProfile>
  >({});
  const [subscriptionPlans, setSubscriptionPlans] = useState<
    Record<number, SubscriptionPlan>
  >({});

  // Calculate selected amount
  const selectedAmount = earnings
    .filter((earning) => selectedEarnings.has(earning.id))
    .reduce((sum, earning) => sum + earning.amount_earned, 0);

  // Fetch pending earnings when dialog opens
  const fetchPendingEarnings = async () => {
    if (!collaboratorId) return;

    setIsLoading(true);
    try {
      // Fetch earnings with related data
      const { data: earningsData, error: earningsError } = await supabase
        .from("collaborator_earnings")
        .select(
          `
          *,
          promotions (
            id,
            name,
            code
          ),
          user_subscriptions (
            id,
            price,
            currency_code,
            user_id,
            subscription_plan_id,
            status,
            subscription_start
          )
        `
        )
        .eq("collaborator_id", parseInt(collaboratorId))
        .is("payout_id", null)
        .order("created_at", { ascending: false });

      if (earningsError) throw earningsError;
      setEarnings(earningsData || []);

      // Fetch customer profiles and subscription plans if we have earnings
      if (earningsData && earningsData.length > 0) {
        // Get unique customer IDs
        const customerIds = Array.from(
          new Set(
            earningsData
              .filter((item) => item.user_subscriptions?.user_id)
              .map((item) => item.user_subscriptions!.user_id)
          )
        );

        // Get unique plan IDs
        const planIds = Array.from(
          new Set(
            earningsData
              .filter((item) => item.user_subscriptions?.subscription_plan_id)
              .map((item) => item.user_subscriptions!.subscription_plan_id)
          )
        );

        // Fetch customer profiles and plans in parallel
        const [customersResponse, plansResponse] = await Promise.all([
          customerIds.length > 0
            ? supabase
                .from("profiles")
                .select("id, name, email, avatar_url")
                .in("id", customerIds)
            : Promise.resolve({ data: [], error: null }),
          planIds.length > 0
            ? supabase
                .from("subscription_plans")
                .select("id, name, duration, duration_type")
                .in("id", planIds)
            : Promise.resolve({ data: [], error: null }),
        ]);

        if (customersResponse.error) throw customersResponse.error;
        if (plansResponse.error) throw plansResponse.error;

        // Create lookup maps
        const customersMap: Record<number, CustomerProfile> = {};
        customersResponse.data?.forEach((customer) => {
          customersMap[customer.id] = customer;
        });
        setCustomerProfiles(customersMap);

        const plansMap: Record<number, SubscriptionPlan> = {};
        plansResponse.data?.forEach((plan) => {
          plansMap[plan.id] = plan;
        });
        setSubscriptionPlans(plansMap);
      }
    } catch (error) {
      console.error("Error fetching pending earnings:", error);
      toast({
        title: "Error",
        description: "Failed to load pending earnings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle dialog open
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      fetchPendingEarnings();
      setSelectedEarnings(new Set()); // Reset selection
    }
  };

  // Handle select all toggle
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedEarnings(new Set(earnings.map((earning) => earning.id)));
    } else {
      setSelectedEarnings(new Set());
    }
  };

  // Handle individual selection
  const handleSelectEarning = (earningId: number, checked: boolean) => {
    const newSelection = new Set(selectedEarnings);
    if (checked) {
      newSelection.add(earningId);
    } else {
      newSelection.delete(earningId);
    }
    setSelectedEarnings(newSelection);
  };

  // Handle payout creation
  const handleCreatePayout = async () => {
    if (selectedEarnings.size === 0) {
      toast({
        title: "No Earnings Selected",
        description: "Please select at least one earning to create a payout.",
        variant: "destructive",
      });
      return;
    }

    setIsCreating(true);
    try {
      // Create the payout record
      const { data: payout, error: payoutError } = await supabase
        .from("collaborator_payouts")
        .insert([
          {
            collaborator_id: parseInt(collaboratorId),
            amount: selectedAmount,
            status: "pending" as const,
          },
        ])
        .select()
        .single();

      if (payoutError) throw payoutError;

      // Update selected earnings to link them to this payout
      const selectedEarningIds = Array.from(selectedEarnings);
      const { error: updateError } = await supabase
        .from("collaborator_earnings")
        .update({ payout_id: payout.id })
        .in("id", selectedEarningIds);

      if (updateError) {
        // If updating earnings fails, delete the payout to maintain consistency
        await supabase
          .from("collaborator_payouts")
          .delete()
          .eq("id", payout.id);
        throw updateError;
      }

      toast({
        title: "Payout Request Created",
        description: `Successfully created payout request for ${formatCurrency(
          selectedAmount
        )} covering ${selectedEarnings.size} earnings.`,
      });

      setIsOpen(false);
      onPayoutCreated();
    } catch (error) {
      console.error("Error creating payout request:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to create payout request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  // Get customer profile for an earning
  const getCustomerProfile = (earning: EarningForSelection) => {
    if (!earning.user_subscriptions?.user_id) return null;
    return customerProfiles[earning.user_subscriptions.user_id] || null;
  };

  // Get subscription plan for an earning
  const getSubscriptionPlan = (earning: EarningForSelection) => {
    if (!earning.user_subscriptions?.subscription_plan_id) return null;
    return (
      subscriptionPlans[earning.user_subscriptions.subscription_plan_id] || null
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          className="bg-amber-600 hover:bg-amber-700"
          disabled={isCreating}
        >
          {isCreating ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Send className="h-4 w-4 mr-2" />
          )}
          Create Payout Request
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-6xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Select Earnings for Payout</DialogTitle>
          <DialogDescription>
            Choose which pending earnings to include in this payout request.
            Total available: {formatCurrency(pendingAmount)}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : earnings.length > 0 ? (
            <>
              {/* Selection Summary */}
              <div className="mb-4 p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Checkbox
                      id="select-all"
                      checked={selectedEarnings.size === earnings.length}
                      onCheckedChange={handleSelectAll}
                    />
                    <label
                      htmlFor="select-all"
                      className="text-sm font-medium cursor-pointer"
                    >
                      Select All ({earnings.length} earnings)
                    </label>
                  </div>
                  <div className="text-sm">
                    <span className="text-muted-foreground">Selected: </span>
                    <span className="font-medium">
                      {selectedEarnings.size} earnings •{" "}
                      {formatCurrency(selectedAmount)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Earnings Table */}
              <div className="flex-1 overflow-auto border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">Select</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Promotion</TableHead>
                      <TableHead>Commission</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {earnings.map((earning) => {
                      const customerProfile = getCustomerProfile(earning);
                      const subscriptionPlan = getSubscriptionPlan(earning);
                      const isSelected = selectedEarnings.has(earning.id);

                      return (
                        <TableRow
                          key={earning.id}
                          className={isSelected ? "bg-muted/50" : ""}
                        >
                          <TableCell>
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={(checked) =>
                                handleSelectEarning(
                                  earning.id,
                                  checked as boolean
                                )
                              }
                            />
                          </TableCell>
                          <TableCell className="text-sm">
                            {format(
                              new Date(earning.created_at),
                              "MMM dd, yyyy"
                            )}
                          </TableCell>
                          <TableCell>
                            {customerProfile ? (
                              <div className="flex items-center gap-2">
                                <Avatar className="h-6 w-6">
                                  <AvatarImage
                                    src={
                                      customerProfile.avatar_url || undefined
                                    }
                                  />
                                  <AvatarFallback>
                                    {customerProfile.name?.charAt(0) || "U"}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <div className="font-medium text-sm">
                                    {customerProfile.name || "Unknown"}
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    {customerProfile.email}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <User className="h-5 w-5 text-muted-foreground" />
                                <span className="text-muted-foreground text-sm">
                                  Unknown
                                </span>
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            {subscriptionPlan ? (
                              <div>
                                <div className="font-medium text-sm">
                                  {subscriptionPlan.name}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {subscriptionPlan.duration}{" "}
                                  {subscriptionPlan.duration_type}
                                </div>
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-sm">
                                Unknown plan
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div>
                              <div className="font-medium text-sm">
                                {earning.promotions?.name || "Unknown"}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {earning.promotions?.code || "No code"}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            {earning.commission_type_at_time_of_transaction ===
                            "percentage"
                              ? `${earning.commission_at_time_of_transaction}%`
                              : formatCurrency(
                                  earning.commission_at_time_of_transaction
                                )}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(earning.amount_earned)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground">
                No pending earnings found.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleCreatePayout}
            disabled={selectedEarnings.size === 0 || isCreating}
          >
            {isCreating ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            Create Payout ({formatCurrency(selectedAmount)})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PayoutSelectionDialog;
